/**
 * @file    useEnginePlugins.ts
 * @scope   ENGINE
 * @summary Composes the per-plugin hooks into single drawer-facing hooks.
 * @consumes ENGINE_PLUGINS
 * @produces useEngineTabNavigation, useEngineTrendSync, useEngineResetSelection
 * @sideEffects Those of the composed plugin hooks.
 * @notes WHY hooks in a loop are safe: `ENGINE_PLUGINS` is a module-level constant, so the call
 *        order is identical on every render.
 */
import { Tab } from 'components/TabNavigator/TabNavigator';
import { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { ActiveEngineDescriptor } from '../hooks/useActiveEngineDescriptor';
import { ENGINE_PLUGINS } from './registry';
import { EngineHostNavigation } from './types';

const DEFAULT_MAX_CRUMBS = 1;

/** TabNavigator handler + breadcrumb depth combining every plugin's cleanup. */
export const useEngineTabNavigation = ({
  currentTab,
  setCurrentTab,
  selectedPartition,
}: Omit<EngineHostNavigation, 'goToMainTab'>) => {
  const cleanups = ENGINE_PLUGINS.map((plugin) =>
    plugin.useTabCleanup({ currentTab, selectedPartition })
  );

  const handleNavigate = (tab: Tab, poppedTabs: Tab[]) => {
    setCurrentTab(tab);
    cleanups.forEach(({ onNavigate }) => onNavigate(tab, poppedTabs));
  };

  const maxCrumbs = Math.max(DEFAULT_MAX_CRUMBS, ...cleanups.map((c) => c.maxCrumbs ?? 0));

  return { handleNavigate, maxCrumbs };
};

/** Mirrors the drawer trend control into the slices of the plugins that need it. */
export const useEngineTrendSync = (
  descriptor: ActiveEngineDescriptor,
  trend?: TrendOptions | null
): void => {
  ENGINE_PLUGINS.forEach((plugin) => plugin.useTrendSync?.(descriptor, trend));
};

/** Callback clearing every plugin selection (used when switching to a legacy chart). */
export const useEngineResetSelection = (): (() => void) => {
  const resets = ENGINE_PLUGINS.map((plugin) => plugin.useResetSelection?.());
  return () => resets.forEach((reset) => reset?.());
};
